import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { CustomerStatementVM } from "./actions/CustomerStatementVM";
import { Button, styled, media, Pagination } from "@payright/web-components";
import { DateTime } from "luxon";
import { fetchStatements } from "./actions/fetchStatements";
import { Metadata } from "./actions/Metadata";

type StatementsTableProps = {
  onViewStatement: (statement: CustomerStatementVM) => void;
  latestStatementDate?: Date;
};

const SCContent = styled.div`
  max-width: 800px;
  width: 100%;
  margin: 0 auto 2rem auto;

  h3 {
    margin-bottom: 1em;
    color: ${(props) => props.theme.colours.blue.base};
  }

  .view-statement-link {
    color: ${(props) => props.theme.colours.plum.base};
    text-decoration: underline;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 16px;
    padding: 0;

    &:hover {
      color: ${(props) => props.theme.colours.blue.base};
    }
  }

  .pr-12 {
    padding-right: 3rem;
  }

  .pr-10 {
    padding-right: 2.5rem;
  }
`;

const SCStatementsTableContainer = styled.div`
  overflow-x: auto;
`;

const SCStatementsTable = styled.table`
  width: 100%;
  max-width: 800px;
  min-width: 600px;
  border-collapse: inherit;
  border-spacing: 0 0.6rem;

  th,
  td {
    text-align: left;
  }

  th {
    padding: 0.8rem 1rem;
    background-color: ${(props) => props.theme.colours.grey.xlight};
    font-weight: 600;
    color: ${(props) => props.theme.colours.blue.base};
    border-radius: 0.4em;

    &:first-child {
      border-bottom-left-radius: 0.4em;
    }
    &:last-child {
      border-bottom-right-radius: 0.4em;
    }
  }

  td {
    padding: 0.8rem 1rem;
    background: white;

    &:first-child {
      border-bottom-left-radius: 0.4em;
      border-top-left-radius: 0.4em;
    }
    &:last-child {
      border-bottom-right-radius: 0.4em;
      border-top-right-radius: 0.4em;
    }
  }

  tr {
    box-shadow: 0.5rem 0 1rem ${(props) => props.theme.colours.grey.light};
    border-radius: 0.8em;
    margin-bottom: 0.66em;

    &:hover {
      box-shadow: 0.5rem 0 1.5rem ${(props) => props.theme.colours.grey.light};
    }

    &.loading {
      animation: loading 1.5s ease-in-out infinite;

      @keyframes loading {
        0% { opacity: 0.4; }
        50% { opacity: 0.7; }
        100% { opacity: 0.4; }
      }
    }
  }

  ${media.min.medium} {
    td, th {
      padding: 1rem 1.86em;
    }
  }
`;

const SCPaginationContainer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

const StatementsTable: React.FC<StatementsTableProps> = ({
  onViewStatement,
  latestStatementDate,
}) => {
  const [statements, setStatements] = useState<CustomerStatementVM[]>([]);
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState<Metadata>({
    currentPage: 1,
    lastPage: 1,
    limit: 10,
    count: 0,
  });

  const refreshStatements = async (page: number, limit: number) => {
    setLoading(true);
    const result = await fetchStatements(page, limit);
    setLoading(false);

    if (!result.success) {
      return;
    }

    setStatements(result.statements);
    setMetadata(result.metadata);
  };

  const handlePageChange = (selectedPage: any) => {
    refreshStatements(selectedPage.selected + 1, metadata.limit);
  };

  useEffect(() => {
    refreshStatements(1, 10);
  }, [latestStatementDate]);

  if (statements.length === 0) {
    return null;
  }

  const loadingRows = new Array(statements.length || 3).fill(null).map((_, i) => (
    <tr className="loading">
      <td>
        <button
          className="view-statement-link"
          disabled
        >
          <span className="pr-12">
            Loading...
          </span>
        </button>
      </td>
      <td className="pr-10">--/--/----</td>
      <td className="pr-10">--/--/----</td>
      <td>
        <Button size="small" colour="blue" outline disabled>
          View
        </Button>
      </td>
    </tr>
  ));

  return (
    <SCContent>
      <h3>Recent Statements</h3>
      <SCStatementsTableContainer>
        <SCStatementsTable>
          <thead>
            <tr>
              <th>Created</th>
              <th>From Date</th>
              <th>To Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && loadingRows}
            {!loading &&
              statements.map((statement) => (
                <tr key={statement.id}>
                  <td>
                    <button
                      className="view-statement-link"
                      onClick={() => onViewStatement(statement)}
                    >
                      {DateTime.fromSQL(statement.createdAt, { zone: "utc" })
                        .toLocal()
                        .toFormat("dd/MM/yyyy HH:mm")}
                    </button>
                  </td>
                  <td>
                    {DateTime.fromISO(statement.fromDate).toFormat(
                      "dd/MM/yyyy"
                    )}
                  </td>
                  <td>
                    {DateTime.fromISO(statement.toDate).toFormat("dd/MM/yyyy")}
                  </td>
                  <td>
                    <Button
                      size="small"
                      colour="blue"
                      outline
                      handleClick={() => onViewStatement(statement)}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </SCStatementsTable>
      </SCStatementsTableContainer>
      {metadata.count > metadata.limit && (
        <SCPaginationContainer>
          <Pagination
            pageCount={metadata.lastPage}
            handlePageChange={handlePageChange}
            currentPage={metadata.currentPage - 1}
          />
        </SCPaginationContainer>
      )}
    </SCContent>
  );
};

const mapStateToProps = (state: any) => ({
  customerId: state.auth?.customerId || localStorage.getItem("customerId"),
});

export default connect(mapStateToProps)(StatementsTable);
